/** Resolve thumbnail URLs for Input Materials (Prompt chip thumbs). */

import { useEffect, useMemo, useState } from 'react'
import { loadImagePickerItemDetailDeduped } from '../canvasSharedConfigLoaders'
import { resolveCanvasImageThumbUrl } from '../canvasImageQuality'
import type { InputMaterial } from '../dataflow'

export function useMaterialThumbMap(workspaceId: string, materials: InputMaterial[]): Map<string, string> {
  const [map, setMap] = useState<Map<string, string>>(() => new Map())
  const signature = useMemo(
    () =>
      materials
        .map((m) => {
          const entityId = 'entityId' in m ? m.entityId || '' : ''
          const generatedOutputId = 'generatedOutputId' in m ? m.generatedOutputId || '' : ''
          const url = 'url' in m ? m.url || '' : ''
          return `${m.edgeId}:${m.kind}:${entityId}:${generatedOutputId}:${url}`
        })
        .join('|'),
    [materials],
  )

  useEffect(() => {
    let cancelled = false
    if (!workspaceId || materials.length === 0) {
      setMap(new Map())
      return
    }

    ;(async () => {
      const next = new Map<string, string>()
      await Promise.all(
        materials.map(async (m) => {
          if (m.kind === 'IMAGE') {
            // VistoeraTASK-00114 forensic — this MUST use the exact same
            // canonical, entityId-first resolver as the working "相关资产"
            // card (`InputMaterialsRow.tsx`'s `useImageThumb`), never a
            // reimplementation with inverted priority. Previously this
            // branch preferred `generatedOutputId` over `entityId`, so
            // once an output was materialized into a permanent SiteAsset
            // (entityId populated alongside a now-stale/broken
            // generatedOutputId content URL) the inline prompt chip
            // pointed at the wrong, often-broken URL while the Related
            // Assets card (entityId-first) rendered fine.
            const directUrl = resolveCanvasImageThumbUrl(workspaceId, {
              entityId: m.entityId,
              generatedOutputId: m.generatedOutputId,
              nodeType: m.nodeType,
            })
            if (directUrl) {
              next.set(m.edgeId, directUrl)
              return
            }
            if (!m.entityId) return
            try {
              const data = await loadImagePickerItemDetailDeduped(workspaceId, `sa:${m.entityId}`, {
                include_usage: false,
                include_variants: false,
              })
              const url = data.thumbnail_url || data.preview_url
              if (url) next.set(m.edgeId, url)
            } catch {
              /* chip degrades without thumb */
            }
          } else if (m.kind === 'VIDEO' && m.url) {
            next.set(m.edgeId, m.url)
          }
        }),
      )
      if (!cancelled) setMap(next)
    })()

    return () => {
      cancelled = true
    }
    // TASK-00058 forensic root cause: `materials` MUST NOT be a dependency
    // here. Callers commonly pass `nodeData.inputMaterials || []` — a
    // brand-new `[]` literal on every render whenever there are no
    // materials — so listing the array itself as a dependency made this
    // effect (and its `setMap(...)` calls) re-fire on EVERY render,
    // forever: setState -> re-render -> new `[]` -> effect re-fires ->
    // setState -> ... React error #185 (Maximum update depth exceeded).
    // Confirmed live on Preview (workspace 97161bbe.../project
    // e7dad90a..., 98 nodes/82 edges: many independent generate_image/
    // generate_video nodes each running this same per-render loop
    // simultaneously pushed the Canvas root over React's synchronous
    // nested-update ceiling within a single commit, blanking the WHOLE
    // Canvas via `CanvasErrorBoundary` even though every node/edge was
    // valid data). `signature` already derives a stable, content-based
    // fingerprint of every field of `materials` this effect actually
    // reads, so it is the only dependency this effect needs — the
    // `materials` array read inside the effect body is still the
    // CURRENT render's value via normal closure capture, just not used
    // to decide whether to re-run.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, signature])

  return map
}
